//@flow
import PjpChartDataBuilder from 'common/entity/pjpMaster/pjpInsights/charts/PjpChartDataBuilder';
import PjpMetricCardDecider from './PjpMetricCardDecider';

import type { PjpGraphResponse } from 'common/models/internal/PjpGraphResponse';
import { type PjpPolarChartDataType } from 'common/entity/pjpMaster/pjpDataTypes/insights/PjpPolarChartDataType';
import DifferentiableColorPicker from 'common/utils/DifferentiableColorPicker';
import type { PjpMetricGraph } from 'common/models/internal/PjpMetricGraph';

export default class PjpPolarChartDataBuilder extends PjpChartDataBuilder {
    metricGraph: PjpMetricGraph;
    filters: Object;
    metric: PjpGraphResponse;
    entityKey: string;
    dataKey: string;

    constructor(
        metricGraph: PjpMetricGraph,
        filters: Object,
        metric: PjpGraphResponse,
        state: any,
        props: any
    ) {
        super(state, props);
        this.metricGraph = metricGraph;
        this.filters = filters;
        this.metric = metric;
    }

    withEntityKey(entityKey: string) {
        this.entityKey = entityKey;
        return this;
    }

    withDataKey(dataKey: string) {
        this.dataKey = dataKey;
        return this;
    }

    auto() {
        const graphQuery = this.metricGraph.getGraphQuery();
        this.dataKey = graphQuery.metric;
        const firstMultiDimensionedGroupBy = graphQuery.groupBy.find(
            groupBy => {
                return PjpMetricCardDecider.isGroupByVariable(
                    this.metricGraph,
                    groupBy,
                    this.filters
                );
            }
        );
        if (!firstMultiDimensionedGroupBy) {
            throw new Error("Can't find multi dimensioned groupBy");
        }
        this.entityKey = firstMultiDimensionedGroupBy;
        return this;
    }

    build(): PjpPolarChartDataType {
        let chartData = [];
        const colorPicker = new DifferentiableColorPicker();
        const metricsData = this.getMetricsDataForGraph(
            this.metricGraph,
            this.metric
        );
        if (!metricsData) {
            return chartData;
        }
        chartData = metricsData.metricDataItems
            .map(metricDataItem => {
                const { metricDataRow } = metricDataItem;

                const graphQuery = this.metricGraph.getGraphQuery();

                const entity = this.getEntityValueForPjpData(
                    metricDataRow,
                    graphQuery.groupBy
                );
                this.entityKey = entity;
                const entityValue = metricDataRow.get(entity);

                const dataValue = metricDataRow.get(this.dataKey);

                let name = null;
                let entityColor = null;
                if (entityValue) {
                    const {
                        label,
                        color
                    } = this.getColorAndLabelForGroupByValue(
                        entityValue.value,
                        this.entityKey,
                        this.dataKey
                    );
                    name = label;
                    entityColor = color;
                }
                if (!entityColor) {
                    entityColor = colorPicker.pick().code;
                }
                return {
                    name,
                    color: entityColor,
                    value: dataValue ? dataValue.value : 0,
                    type: this.convertValueDataType(
                        dataValue ? dataValue.valueDataType : 'INT'
                    ),
                    dataKey: this.dataKey
                };
            })
            .toJS();
        if (chartData.length === 1) {
            if (!chartData[0].value) {
                // Empty data sets might return a single metric row without the entity, filtering it out
                chartData = [];
            }
        }
        return chartData;
    }
}
